import { getCurrentAxiosRequestInterceptor } from '@makemydeal/dr-shared-network';
import { X_DR_DEALER_ID } from '@makemydeal/dr-shared-bff-types';

import { DEAL_ASSISTANT_REQUEST_TIMEOUT_MS } from '../constants';

export type DealAssistantFetch = (input: string, init?: RequestInit) => Promise<Response>;

export type CreateDealAssistantFetchParams = {
    getDealerId: () => number | undefined;
    baseFetch?: DealAssistantFetch;
    timeoutMs?: number;
};

export class DealAssistantTimeoutError extends Error {
    constructor() {
        super('The Deal Assistant request timed out');
        this.name = 'DealAssistantTimeoutError';
    }
}

export class DealAssistantDealerMissingError extends Error {
    constructor() {
        super('The Deal Assistant request needs a dealer id and none is available');
        this.name = 'DealAssistantDealerMissingError';
    }
}

export class DealAssistantAuthError extends Error {
    readonly cause?: unknown;

    constructor(cause?: unknown) {
        super('The auth interceptor is not set up or did not recognize the BFF url');
        this.name = 'DealAssistantAuthError';
        this.cause = cause;
    }
}

const isPositiveInteger = (value: unknown): value is number => Number.isInteger(value) && (value as number) > 0;

const toHeaderRecord = (headers: HeadersInit | undefined): Record<string, string> => {
    if (Array.isArray(headers)) {
        return Object.fromEntries(headers);
    }
    if (typeof (headers as Headers | undefined)?.forEach === 'function') {
        const record: Record<string, string> = {};
        (headers as Headers).forEach((value, key) => {
            record[key] = value;
        });
        return record;
    }
    return { ...(headers as Record<string, string> | undefined) };
};

const getAuthHeaders = async (url: string): Promise<Record<string, string>> => {
    let headers: Record<string, string> | undefined;
    try {
        ({ headers } = await getCurrentAxiosRequestInterceptor()({ url }));
    } catch (error) {
        throw new DealAssistantAuthError(error);
    }
    if (!headers) {
        throw new DealAssistantAuthError();
    }
    return headers;
};

const readText = async (stream: ReadableStream<Uint8Array>): Promise<string> => {
    const reader = stream.getReader();
    const decoder = new TextDecoder();
    let text = '';
    for (;;) {
        const { done, value } = await reader.read();
        if (done) {
            return text + decoder.decode();
        }
        text += decoder.decode(value, { stream: true });
    }
};

const ignoreRejection = (promise: Promise<unknown>) => promise.catch(() => undefined);

const withQuietCancel = (stream: ReadableStream<Uint8Array>) => ({
    getReader: () =>
        new Proxy(stream.getReader(), {
            get(reader, property) {
                if (property === 'cancel') {
                    return (reason?: unknown) => ignoreRejection(reader.cancel(reason));
                }
                const value = Reflect.get(reader, property, reader);
                return typeof value === 'function' ? value.bind(reader) : value;
            }
        })
});

export const createDealAssistantFetch =
    ({
        getDealerId,
        baseFetch = (input, init) => fetch(input, init),
        timeoutMs = DEAL_ASSISTANT_REQUEST_TIMEOUT_MS
    }: CreateDealAssistantFetchParams): DealAssistantFetch =>
    async (input, init) => {
        if (init?.signal?.aborted) {
            throw new DOMException('The Deal Assistant request was aborted', 'AbortError');
        }
        const dealerId = getDealerId();
        if (!isPositiveInteger(dealerId)) {
            throw new DealAssistantDealerMissingError();
        }
        const controller = new AbortController();
        const callerSignal = init?.signal;
        let timedOut = false;
        let failStream: (() => void) | undefined;

        const abortWithCaller = () => controller.abort();
        let timer: ReturnType<typeof setTimeout>;
        const deadline = new Promise<never>((_resolve, reject) => {
            timer = setTimeout(() => {
                timedOut = true;
                controller.abort();
                failStream?.();
                reject(new DealAssistantTimeoutError());
            }, timeoutMs);
        });
        deadline.catch(() => undefined);
        const cleanup = () => {
            clearTimeout(timer);
            callerSignal?.removeEventListener('abort', abortWithCaller);
        };

        callerSignal?.addEventListener('abort', abortWithCaller, { once: true });

        const keepAliveUntilBodyEnds = (response: Response, body: ReadableStream<Uint8Array>): Response => {
            const reader = body.getReader();
            const stream = new ReadableStream<Uint8Array>({
                start(streamController) {
                    failStream = () => {
                        streamController.error(new DealAssistantTimeoutError());
                        void ignoreRejection(reader.cancel());
                    };
                },
                async pull(streamController) {
                    try {
                        const { done, value } = await reader.read();
                        if (done) {
                            cleanup();
                            streamController.close();
                        } else {
                            streamController.enqueue(value);
                        }
                    } catch (error) {
                        cleanup();
                        streamController.error(timedOut ? new DealAssistantTimeoutError() : error);
                    }
                },
                cancel(reason) {
                    cleanup();
                    return reader.cancel(reason);
                }
            });

            return new Proxy(response, {
                get(target, property) {
                    if (property === 'body') {
                        return withQuietCancel(stream);
                    }
                    if (property === 'text') {
                        return () => readText(stream);
                    }
                    const value = Reflect.get(target, property, target);
                    return typeof value === 'function' ? value.bind(target) : value;
                }
            });
        };

        try {
            const headers = await Promise.race([getAuthHeaders(input), deadline]);
            const response = await baseFetch(input, {
                ...init,
                headers: {
                    ...toHeaderRecord(init?.headers),
                    ...headers,
                    [X_DR_DEALER_ID]: String(dealerId)
                },
                signal: controller.signal
            });
            if (!response.body) {
                cleanup();
                return response;
            }
            return keepAliveUntilBodyEnds(response, response.body);
        } catch (error) {
            cleanup();
            throw timedOut ? new DealAssistantTimeoutError() : error;
        }
    };
